import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { PROJECT_SECTION_ICONS } from '../utils/concept-icons';
import { notePreview } from '../utils/note-utils';
import { takeMostRecent } from '../utils/sort-by-updated-at';
import { ProjectSectionHub } from './ProjectSectionHub';
import { SectionHubCard, type SectionHubItem } from './SectionHubCard';

const RECENT_LIMIT = 5;
const NOTE_PREVIEW_MAX_LEN = 80;

interface ResourceListItem {
  id: string;
  title: string;
  updatedAt: string;
}

interface NoteListItem {
  id: string;
  content: string;
  updatedAt: string;
}

export const DocumentationLanding: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { token } = useAuth();
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [resourceItems, setResourceItems] = useState<ResourceListItem[]>([]);
  const [noteItems, setNoteItems] = useState<NoteListItem[]>([]);

  useEffect(() => {
    if (!projectId || !token) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      try {
        const [resourcesRes, notesRes] = await Promise.all([
          fetch(`${API_BASE}/api/projects/${projectId}/resources`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE}/api/projects/${projectId}/notes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const resourcesData = resourcesRes.ok ? await resourcesRes.json() : { resources: [] };
        const notesData = notesRes.ok ? await notesRes.json() : { notes: [] };

        if (cancelled) {
          return;
        }

        setResourceItems((resourcesData.resources || []) as ResourceListItem[]);
        setNoteItems((notesData.notes || []) as NoteListItem[]);
      } catch {
        if (!cancelled) {
          setResourceItems([]);
          setNoteItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId, token]);

  const resources: SectionHubItem[] = takeMostRecent(resourceItems, RECENT_LIMIT).map(
    (resource) => ({
      id: resource.id,
      label: resource.title || t('untitled'),
      href: `/projects/${projectId}/resources/${resource.id}`,
    })
  );

  const notes: SectionHubItem[] = takeMostRecent(noteItems, RECENT_LIMIT).map((note) => ({
    id: note.id,
    label: notePreview(note.content, NOTE_PREVIEW_MAX_LEN) || t('no_content'),
    href: `/projects/${projectId}/notes?noteId=${encodeURIComponent(note.id)}`,
  }));

  if (!projectId) {
    return null;
  }

  return (
    <ProjectSectionHub
      title={t('explorer_documentation_section')}
      titleIcon={PROJECT_SECTION_ICONS.documentation}
      intro={t('documentation_landing_intro')}
      loading={loading}
    >
      <SectionHubCard
        title={t('Resources')}
        icon={PROJECT_SECTION_ICONS.resources}
        href={`/projects/${projectId}/resources`}
        emptyDescription={t('resources_empty_description')}
        items={resources}
        itemsHeading={t('section_hub_most_recent_resources')}
      />
      <SectionHubCard
        title={t('Notes')}
        icon={PROJECT_SECTION_ICONS.notes}
        href={`/projects/${projectId}/notes`}
        emptyDescription={t('notes_empty_description')}
        items={notes}
        itemsHeading={t('section_hub_most_recent_notes')}
      />
    </ProjectSectionHub>
  );
};
